import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Modal } from '@heroui/react';
import { MOD_KEY } from '@/lib/platform';

/**
 * Cmd/Ctrl+/ keyboard shortcut cheat-sheet. Mounted once at the app shell;
 * listens globally so any focused element can pop it.
 *
 * Nielsen heuristic 10 (Help & Documentation) was scoring 1/4 — the only
 * keyboard hint anywhere lived in the QuickSwitcher input placeholder.
 * This overlay surfaces every shortcut in one place, kbd-styled to feel
 * like a real cheat-sheet instead of a docs page.
 */

const cmd = MOD_KEY;

function buildGroups(t: TFunction): Array<{
  title: string;
  rows: Array<{ keys: string[]; label: string }>;
}> {
  return [
    {
      title: t('prof.shortcuts.navTitle'),
      rows: [
        { keys: [cmd, 'K'], label: t('prof.shortcuts.quickSwitcher') },
        { keys: ['J'], label: t('prof.shortcuts.jumpMention') },
        { keys: [cmd, '/'], label: t('prof.shortcuts.showCheatSheet') },
        { keys: ['Esc'], label: t('prof.shortcuts.closeModal') },
      ],
    },
    {
      title: t('prof.shortcuts.chatTitle'),
      rows: [
        { keys: ['Enter'], label: t('prof.shortcuts.sendMessage') },
        { keys: ['Shift', 'Enter'], label: t('prof.shortcuts.newLine') },
        { keys: ['@'], label: t('prof.shortcuts.mention') },
        { keys: [':name:'], label: t('prof.shortcuts.emoji') },
      ],
    },
    {
      title: t('prof.shortcuts.formatTitle'),
      rows: [
        { keys: [cmd, 'B'], label: t('prof.shortcuts.bold') },
        { keys: [cmd, 'I'], label: t('prof.shortcuts.italic') },
        { keys: [cmd, 'U'], label: t('prof.shortcuts.underline') },
        { keys: [cmd, 'Shift', 'X'], label: t('prof.shortcuts.strike') },
        { keys: [cmd, 'E'], label: t('prof.shortcuts.code') },
        { keys: [cmd, 'Shift', '.'], label: t('prof.shortcuts.spoiler') },
        { keys: [cmd, 'Shift', '9'], label: t('prof.shortcuts.quote') },
        { keys: [cmd, 'K'], label: t('prof.shortcuts.link') },
      ],
    },
    {
      title: t('prof.shortcuts.voiceTitle'),
      rows: [
        { keys: [cmd, 'D'], label: t('prof.shortcuts.toggleMic') },
        { keys: [cmd, 'Shift', 'D'], label: t('prof.shortcuts.toggleSpeakers') },
      ],
    },
  ];
}

export function ShortcutsOverlay() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const groups = buildGroups(t);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Cmd/Ctrl + slash. We also accept Shift+/ which produces "?" on
      // most keyboards, because that's the macOS convention.
      if ((e.metaKey || e.ctrlKey) && (e.key === '/' || e.key === '?')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Modal.Backdrop isOpen={open} onOpenChange={setOpen}>
      <Modal.Container size="md" scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('prof.shortcuts.title')}</Modal.Heading>
            <p className="mt-1.5 text-sm text-muted">{t('prof.shortcuts.closeHint')}</p>
          </Modal.Header>
          <Modal.Body>
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="mb-2 font-medium text-ink-muted">
                {group.title}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {group.rows.map((row) => (
                  <li
                    key={row.label}
                    className="rounded-xl transition-colors hover:bg-default/50 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 flex items-center justify-between gap-3 px-2 py-1"
                  >
                    <span className="text-sm font-light text-ink-dim">{row.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {row.keys.map((k, i) => (
                        <kbd
                          key={`${row.label}-${i}`}
                          className="rounded-lg px-1.5 py-0.5 font-mono text-[11px] text-ink"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.06)',
                            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
                          }}
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
